import { useState, useEffect, useCallback, useRef } from 'react';
import { useDropzone, FileRejection, Accept } from 'react-dropzone';
import { X, Upload, Sparkles, AlertCircle, Check, ArrowRight, FolderPlus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { getFileIconByName } from '../FileIcons';
import { casesApi, documentsApi, redactionApi, isContainerRedactionResponse, Document as ApiDocument } from '../../lib/api';
import type { Case } from '../../types';

type ModalStep = 'form' | 'upload' | 'processing' | 'complete';

interface CreateCaseModalProps {
  open: boolean;
  onClose: () => void;
  onCaseCreated: (newCase: Case) => void;
}

interface FileUploadState {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed' | 'detecting' | 'ready';
  document?: ApiDocument;
  jobId?: string;
  error?: string;
  detectionProgress?: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export function CreateCaseModal({ open, onClose, onCaseCreated }: CreateCaseModalProps) {
  const [step, setStep] = useState<ModalStep>('form');
  const [caseName, setCaseName] = useState('');
  const [caseNotes, setCaseNotes] = useState('');
  const [createdCase, setCreatedCase] = useState<Case | null>(null);
  const [isCreatingCase, setIsCreatingCase] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Upload state
  const [fileStates, setFileStates] = useState<FileUploadState[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const pollingRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const maxFileSize = 100 * 1024 * 1024; // 100MB
  const maxFiles = 10;

  // Accepted file types
  const acceptedFileTypes: Accept = {
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'text/plain': ['.txt'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
    'text/csv': ['.csv'],
    'message/rfc822': ['.eml'],
    'application/vnd.ms-outlook': ['.msg'],
    'application/vnd.ms-outlook-pst': ['.pst'],
    'application/octet-stream': ['.pst'],  // PST often comes as octet-stream
    'video/mp4': ['.mp4'],
    'video/x-msvideo': ['.avi'],
    'video/avi': ['.avi'],
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setStep('form');
      setCaseName('');
      setCaseNotes('');
      setCreatedCase(null);
      setIsCreatingCase(false);
      setCreateError(null);
      setFileStates([]);
      setIsUploading(false);
      Object.values(pollingRef.current).forEach(clearInterval);
      pollingRef.current = {};
    }
  }, [open]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingRef.current).forEach(clearInterval);
    };
  }, []);

  const handleCreateCase = async () => {
    if (!caseName.trim()) return;

    setIsCreatingCase(true);
    setCreateError(null);

    try {
      const newCase = await casesApi.createCase({
        name: caseName.trim(),
        notes: caseNotes.trim() || undefined,
      });
      setCreatedCase(newCase);
      setStep('upload');
    } catch (err: any) {
      console.error('Failed to create case:', err);
      setCreateError(err.response?.data?.detail || 'Failed to create case. Please try again.');
    } finally {
      setIsCreatingCase(false);
    }
  };

  // File drop handler
  const onDrop = useCallback((acceptedFiles: File[], _rejectedFiles: FileRejection[]) => {
    if (acceptedFiles.length > 0) {
      const newStates: FileUploadState[] = acceptedFiles.map((file) => ({
        file,
        progress: 0,
        status: 'pending' as const,
      }));
      setFileStates((prev) => [...prev, ...newStates]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive, open: openFileDialog } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxSize: maxFileSize,
    maxFiles: maxFiles - fileStates.length,
    multiple: true,
    noClick: true,
    noKeyboard: false,
  });

  const handleRemoveFile = (index: number) => {
    const state = fileStates[index];
    if (state.jobId && pollingRef.current[state.jobId]) {
      clearInterval(pollingRef.current[state.jobId]);
      delete pollingRef.current[state.jobId];
    }
    setFileStates((files) => files.filter((_, i) => i !== index));
  };

  // Upload file
  const uploadFile = async (file: File, index: number, caseId: string) => {
    try {
      const onUploadProgress = (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setFileStates((prev) => {
          const updated = [...prev];
          updated[index].progress = percentCompleted;
          return updated;
        });
      };

      const documentResponse = await documentsApi.uploadFile(file, {
        case_id: caseId,
        onUploadProgress,
      });

      const { message, ...document } = documentResponse;

      setFileStates((prev) => {
        const updated = [...prev];
        updated[index].status = 'completed';
        updated[index].document = document as ApiDocument;
        updated[index].progress = 100;
        return updated;
      });

      return document as ApiDocument;
    } catch (error: any) {
      console.error(`Failed to upload file ${file.name}:`, error);
      setFileStates((prev) => {
        const updated = [...prev];
        updated[index].status = 'failed';
        updated[index].error = error.response?.data?.detail || error.message || 'Upload failed';
        return updated;
      });
      throw error;
    }
  };

  // Start AI detection
  const startAIDetection = async (document: ApiDocument, index: number) => {
    try {
      setFileStates((prev) => {
        const updated = [...prev];
        updated[index].status = 'detecting';
        updated[index].detectionProgress = 'Starting AI detection...';
        return updated;
      });

      const response = await redactionApi.createJob({
        document_id: document.id,
        auto_approve: false,
      });

      // Check if this is a container response (PST file with multiple child jobs)
      if (isContainerRedactionResponse(response)) {
        // Container documents (PST) create jobs for all children automatically
        // Mark as ready immediately - children will be processed separately
        const totalEntities = response.jobs.reduce((sum, j) => sum + j.entities_detected, 0);
        setFileStates((prev) => {
          const updated = [...prev];
          updated[index].status = 'ready';
          updated[index].detectionProgress = `${response.jobs_created} child documents analyzed (${totalEntities} items found)`;
          return updated;
        });
      } else {
        // Regular document - poll for single job completion
        setFileStates((prev) => {
          const updated = [...prev];
          updated[index].jobId = response.job_id;
          updated[index].detectionProgress = 'Analyzing document...';
          return updated;
        });

        pollJobStatus(response.job_id, index);
      }
    } catch (error: any) {
      console.error(`Failed to start AI detection for ${document.name}:`, error);
      setFileStates((prev) => {
        const updated = [...prev];
        updated[index].status = 'ready';
        updated[index].detectionProgress = 'AI detection failed - manual redaction available';
        return updated;
      });
    }
  };

  // Poll job status
  const pollJobStatus = (jobId: string, index: number) => {
    const poll = async () => {
      try {
        const status = await redactionApi.getJobStatus(jobId);

        setFileStates((prev) => {
          const updated = [...prev];
          if (status.status === 'awaiting_approval') {
            updated[index].status = 'ready';
            updated[index].detectionProgress = `Found ${status.entities_detected || 0} sensitive items`;
            if (pollingRef.current[jobId]) {
              clearInterval(pollingRef.current[jobId]);
              delete pollingRef.current[jobId];
            }
          } else if (status.status === 'failed') {
            updated[index].status = 'ready';
            updated[index].detectionProgress = 'AI detection failed - manual redaction available';
            if (pollingRef.current[jobId]) {
              clearInterval(pollingRef.current[jobId]);
              delete pollingRef.current[jobId];
            }
          } else {
            updated[index].detectionProgress = `Analyzing... (${status.progress || 0}%)`;
          }
          return updated;
        });
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };

    pollingRef.current[jobId] = setInterval(poll, 2000);
    poll();
  };

  // Start upload and detection
  const startUploadAndDetection = async () => {
    if (!createdCase || fileStates.length === 0) return;

    setStep('processing');
    setIsUploading(true);

    setFileStates((prev) =>
      prev.map((state) => ({
        ...state,
        status: 'uploading' as const,
        progress: 0,
      }))
    );

    const uploadPromises = fileStates.map((state, index) =>
      uploadFile(state.file, index, createdCase.id).catch((err) => {
        console.error(`Upload failed for ${state.file.name}:`, err);
        return null;
      })
    );

    try {
      const documents = await Promise.all(uploadPromises);
      setIsUploading(false);

      const detectionPromises = documents.map((doc, index) => {
        if (doc) {
          return startAIDetection(doc, index);
        }
        return Promise.resolve();
      });

      await Promise.all(detectionPromises);
    } catch (error) {
      console.error('Error during upload/detection:', error);
      setIsUploading(false);
    }
  };

  // Skip upload step
  const handleSkipUpload = () => {
    if (createdCase) {
      onCaseCreated(createdCase);
    }
  };

  // Complete and go to editor
  const handleComplete = () => {
    if (createdCase) {
      onCaseCreated(createdCase);
    }
  };

  // Check states
  const allFilesReady =
    fileStates.length > 0 &&
    fileStates.every((state) => state.status === 'ready' || state.status === 'failed');
  const anyDetecting = fileStates.some((state) => state.status === 'detecting');

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg w-[800px] max-h-[85vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <FolderPlus className="h-5 w-5 text-[#7035C4]" />
            <h2 className="text-lg font-semibold">
              {step === 'form' && 'Create New Case'}
              {step === 'upload' && 'Add Documents'}
              {step === 'processing' && 'Processing Documents'}
              {step === 'complete' && 'Case Created'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Form */}
          {step === 'form' && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="case-name">Case Name *</Label>
                <Input
                  id="case-name"
                  type="text"
                  placeholder="Enter case name"
                  value={caseName}
                  onChange={(e) => setCaseName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="case-notes">Notes (Optional)</Label>
                <Textarea
                  id="case-notes"
                  placeholder="Add any notes about this case..."
                  value={caseNotes}
                  onChange={(e) => setCaseNotes(e.target.value)}
                  rows={4}
                />
              </div>
              {createError && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {createError}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Dropzone */}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragActive ? 'border-[#7035C4] bg-[#f9f5ff]' : 'border-gray-300 hover:border-[#7035C4]'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="h-10 w-10 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {isDragActive ? 'Drop files here...' : 'Drag and drop files here, or'}
                </p>
                <Button variant="outline" onClick={openFileDialog}>
                  Browse Files
                </Button>
                <p className="text-xs text-gray-500 mt-3">
                  DOCX, PDF, XLSX, CSV, EML, MSG, MP4, AVI, TXT • Max {formatFileSize(maxFileSize)} per file
                </p>
              </div>

              {/* File list */}
              {fileStates.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Selected Files ({fileStates.length})</h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {fileStates.map((fileState, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {getFileIconByName(fileState.file.name)}
                          <div>
                            <p className="text-sm font-medium truncate max-w-[300px]">
                              {fileState.file.name}
                            </p>
                            <p className="text-xs text-gray-500">{formatFileSize(fileState.file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* Step 3: Processing */}
          {step === 'processing' && (
            <div className="space-y-4">
              {fileStates.map((fileState, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      {getFileIconByName(fileState.file.name)}
                      <div>
                        <p className="text-sm font-medium">{fileState.file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(fileState.file.size)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {fileState.status === 'uploading' && (
                        <span className="text-xs text-gray-600">Uploading... {fileState.progress}%</span>
                      )}
                      {fileState.status === 'completed' && (
                        <Check className="h-4 w-4 text-[#7035C4]" />
                      )}
                      {fileState.status === 'detecting' && (
                        <>
                          <Sparkles className="h-4 w-4 text-[#7035C4] animate-pulse" />
                          <span className="text-xs text-[#7035C4]">{fileState.detectionProgress}</span>
                        </>
                      )}
                      {fileState.status === 'ready' && (
                        <>
                          <Check className="h-4 w-4 text-green-500" />
                          <span className="text-xs text-green-600">{fileState.detectionProgress}</span>
                        </>
                      )}
                      {fileState.status === 'failed' && (
                        <>
                          <AlertCircle className="h-4 w-4 text-red-500" />
                          <span className="text-xs text-red-600">{fileState.error}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {(fileState.status === 'uploading' || fileState.status === 'detecting') && (
                    <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          fileState.status === 'detecting' ? 'bg-[#7035C4] animate-pulse w-full' : 'bg-[#7035C4]'
                        }`}
                        style={{
                          width: fileState.status === 'uploading' ? `${fileState.progress}%` : '100%',
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 'complete' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Case Created Successfully!</h3>
              <p className="text-gray-600 mb-6">
                Your case "{createdCase?.name}" has been created with {fileStates.filter((f) => f.status === 'ready').length} document(s).
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div>
            {step === 'upload' && (
              <button
                onClick={handleSkipUpload}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Skip, add documents later
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>

            {step === 'form' && (
              <Button
                onClick={handleCreateCase}
                disabled={!caseName.trim() || isCreatingCase}
                className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
              >
                {isCreatingCase ? 'Creating...' : 'Create Case'}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}

            {step === 'upload' && (
              <Button
                onClick={startUploadAndDetection}
                disabled={fileStates.length === 0}
                className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Upload & Analyze
              </Button>
            )}

            {step === 'processing' && (
              <Button
                onClick={() => setStep('complete')}
                disabled={!allFilesReady}
                className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
              >
                {isUploading || anyDetecting ? (
                  <>Processing...</>
                ) : (
                  <>Continue</>
                )}
              </Button>
            )}

            {step === 'complete' && (
              <Button
                onClick={handleComplete}
                className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white"
              >
                Go to Case Editor
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
